import React, { useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbButton,
  WxbCheckbox,
  WxbCollapse,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbPopconfirm,
  WxbSegmented,
  WxbSelect,
  WxbTextarea,
} from '../wxb-ui';
import CycleRuleEditor, {
  createDefaultCycleEvent,
  createDefaultCycleRule,
  CycleEventReferenceEditor,
} from './CycleRuleEditor';
import {
  isPredictableCycleDueRule,
  validateCyclePolicyLocally,
} from './equipmentStateCatalogModel';
import type {
  EquipmentCycleBaselineCapture,
  EquipmentCycleBaselineSource,
  EquipmentCycleExpiryRule,
  EquipmentCyclePolicy,
  EquipmentCycleTimeExpression,
  EquipmentMonthEndPolicy,
  EquipmentStateAction,
  EquipmentStateAttribute,
  EquipmentStateIssueHardness,
  EquipmentStateIssueSeverity,
} from '../../types/prodEquipmentStateCatalog';

interface Props {
  open: boolean;
  initialPolicy: EquipmentCyclePolicy | null;
  existingCodes: string[];
  attributes: EquipmentStateAttribute[];
  actions: EquipmentStateAction[];
  readOnly?: boolean;
  overrideBase?: EquipmentCyclePolicy | null;
  onCancel: () => void;
  onSave: (policy: EquipmentCyclePolicy) => void;
}

type PolicyView = 'start' | 'refresh' | 'expiry';

const severityRank: Record<EquipmentStateIssueSeverity, number> = { info: 0, warning: 1, critical: 2 };

const createElapsedWindow = (): EquipmentCycleTimeExpression => ({
  basis: 'ELAPSED_DURATION', value: 1, unit: 'DAY', monthEndPolicy: null,
});

export const createDefaultCyclePolicy = (): EquipmentCyclePolicy => ({
  code: '',
  name: '',
  startRule: {
    kind: 'EVENT',
    event: { kind: 'CLASS_ACTIVATION' },
    retriggerPolicy: 'IGNORE_WHILE_ACTIVE',
    baselineCaptures: [],
  },
  refreshRule: {
    kind: 'RESET_ON_EVENT',
    event: { kind: 'CYCLE_SATISFIED' },
    recaptureBaselines: true,
  },
  expiryRule: createDefaultCycleRule('ELAPSED'),
  calculationContext: {
    timeZone: 'Asia/Shanghai',
    dstGapPolicy: 'SHIFT_FORWARD',
    dstOverlapPolicy: 'EARLIER_OFFSET',
    adjustmentCalendarCode: null,
    businessDayAdjustment: 'NONE',
  },
  window: { warningBefore: null, dueTolerance: null, gracePeriod: null },
  enforcement: {
    constraintCode: '',
    severity: 'critical',
    hardOrSoft: 'hard',
    violationMessageTemplate: '资源 {resourceCode} 的周期策略 {policyCode} 已到期（dueAt={dueAt}）。',
    activationPoint: 'DUE',
  },
  note: null,
});

const clonePolicy = (policy: EquipmentCyclePolicy): EquipmentCyclePolicy => (
  JSON.parse(JSON.stringify(policy)) as EquipmentCyclePolicy
);

const isRollableRule = (rule: EquipmentCycleExpiryRule): boolean => {
  if (['ELAPSED', 'CALENDAR', 'SCHEDULE'].includes(rule.kind)) return true;
  return rule.kind === 'COMPOSITE' && rule.rules.every(isRollableRule);
};

const usesCalendar = (rule: EquipmentCycleExpiryRule): boolean => {
  if (rule.kind === 'CALENDAR') return true;
  if (rule.kind === 'SCHEDULE') return true;
  return rule.kind === 'COMPOSITE' && rule.rules.some(usesCalendar);
};

const hasAccumulatedRule = (rule: EquipmentCycleExpiryRule): boolean => (
  rule.kind === 'ACCUMULATED'
  || (rule.kind === 'COMPOSITE' && rule.rules.some(hasAccumulatedRule))
);

const syncAccumulatedBaselines = (
  rule: EquipmentCycleExpiryRule,
  captures: EquipmentCycleBaselineCapture[],
): { rule: EquipmentCycleExpiryRule; captures: EquipmentCycleBaselineCapture[] } => {
  const nextCaptures = captures.map((capture) => ({ ...capture, source: { ...capture.source } }));
  const usedKeys = new Set(nextCaptures.map((capture) => capture.key));
  let sequence = 1;
  const visit = (item: EquipmentCycleExpiryRule): EquipmentCycleExpiryRule => {
    if (item.kind === 'ACCUMULATED') {
      let baselineKey = item.baselineKey.trim();
      if (!baselineKey) {
        let candidate = `USAGE_BASELINE_${sequence}`;
        sequence += 1;
        while (usedKeys.has(candidate)) {
          candidate = `USAGE_BASELINE_${sequence}`;
          sequence += 1;
        }
        baselineKey = candidate;
      }
      const index = nextCaptures.findIndex((capture) => capture.key === baselineKey);
      const capture = { key: baselineKey, source: { ...item.source } } as EquipmentCycleBaselineCapture;
      if (index >= 0) nextCaptures[index] = capture;
      else nextCaptures.push(capture);
      usedKeys.add(baselineKey);
      return { ...item, baselineKey };
    }
    if (item.kind === 'COMPOSITE') return { ...item, rules: item.rules.map(visit) };
    return item;
  };
  return { rule: visit(rule), captures: nextCaptures };
};

interface OptionalCycleTimeProps {
  label: string;
  value: EquipmentCycleTimeExpression | null;
  disabled: boolean;
  onChange: (value: EquipmentCycleTimeExpression | null) => void;
}

const OptionalCycleTime: React.FC<OptionalCycleTimeProps> = ({ label, value, disabled, onChange }) => (
  <div className="esc-optional-temporal">
    <WxbCheckbox checked={Boolean(value)} disabled={disabled} onChange={(checked) => onChange(checked ? createElapsedWindow() : null)}>
      启用{label}
    </WxbCheckbox>
    {value && (
      <div className="esc-form-grid esc-form-grid-three">
        <WxbSelect
          label={`${label}口径`}
          value={value.basis}
          disabled={disabled}
          options={[
            { value: 'ELAPSED_DURATION', label: '连续时长' },
            { value: 'CALENDAR_PERIOD', label: '日历周期' },
          ]}
          onChange={(basis) => onChange(basis === 'CALENDAR_PERIOD'
            ? { basis, value: 1, unit: 'DAY', monthEndPolicy: null }
            : createElapsedWindow())}
        />
        <WxbInputNumber
          label="数值"
          min={value.basis === 'CALENDAR_PERIOD' ? 1 : 0.000001}
          precision={value.basis === 'CALENDAR_PERIOD' ? 0 : undefined}
          value={value.value}
          disabled={disabled}
          onChange={(nextValue) => onChange({ ...value, value: typeof nextValue === 'number' ? nextValue : 1 })}
        />
        <WxbSelect
          label="单位"
          value={value.unit}
          disabled={disabled}
          options={value.basis === 'CALENDAR_PERIOD'
            ? [
              { value: 'DAY', label: '自然日' }, { value: 'WEEK', label: '自然周' },
              { value: 'MONTH', label: '自然月' }, { value: 'QUARTER', label: '自然季度' },
              { value: 'YEAR', label: '自然年' },
            ]
            : [
              { value: 'SECOND', label: '秒' }, { value: 'MINUTE', label: '分钟' },
              { value: 'HOUR', label: '小时' }, { value: 'DAY', label: '连续天' },
            ]}
          onChange={(unit) => {
            if (value.basis === 'ELAPSED_DURATION') {
              onChange({ ...value, unit: unit as typeof value.unit });
              return;
            }
            const typedUnit = unit as typeof value.unit;
            onChange({
              ...value,
              unit: typedUnit,
              monthEndPolicy: ['MONTH', 'QUARTER', 'YEAR'].includes(typedUnit)
                ? value.monthEndPolicy ?? 'CLAMP'
                : null,
            });
          }}
        />
        {value.basis === 'CALENDAR_PERIOD' && ['MONTH', 'QUARTER', 'YEAR'].includes(value.unit) && (
          <WxbSelect
            label="月末处理"
            value={value.monthEndPolicy ?? 'CLAMP'}
            disabled={disabled}
            options={[
              { value: 'CLAMP', label: '截到目标月最后一天' },
              { value: 'PRESERVE_END_OF_MONTH', label: '起点为月末时保持月末' },
              { value: 'ROLL_FORWARD', label: '顺延超出天数' },
              { value: 'SKIP', label: '日期不存在时跳过' },
            ]}
            onChange={(monthEndPolicy) => onChange({
              ...value,
              monthEndPolicy: monthEndPolicy as EquipmentMonthEndPolicy,
            })}
          />
        )}
      </div>
    )}
  </div>
);

const CyclePolicyModal: React.FC<Props> = ({
  open,
  initialPolicy,
  existingCodes,
  attributes,
  actions,
  readOnly = false,
  overrideBase = null,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState<EquipmentCyclePolicy>(createDefaultCyclePolicy());
  const [view, setView] = useState<PolicyView>('start');
  const [submitted, setSubmitted] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState('');

  useEffect(() => {
    if (!open) return;
    const source = clonePolicy(initialPolicy ?? overrideBase ?? createDefaultCyclePolicy());
    const initial = isPredictableCycleDueRule(source.expiryRule) ? source : {
      ...source,
      window: { ...source.window, warningBefore: null },
    };
    setDraft(initial);
    setInitialSnapshot(JSON.stringify(initial));
    setView('start');
    setSubmitted(false);
  }, [initialPolicy, open, overrideBase]);

  const problems = useMemo(() => validateCyclePolicyLocally(draft), [draft]);
  const dirty = JSON.stringify(draft) !== initialSnapshot;
  const duplicateCode = submitted && !initialPolicy && !overrideBase
    && existingCodes.map((code) => code.toUpperCase()).includes(draft.code.trim().toUpperCase());
  const overrideViolation = submitted && overrideBase && (
    draft.code.trim().toUpperCase() !== overrideBase.code
    || draft.enforcement.constraintCode.trim().toUpperCase() !== overrideBase.enforcement.constraintCode
    || (overrideBase.enforcement.hardOrSoft === 'hard' && draft.enforcement.hardOrSoft !== 'hard')
    || severityRank[draft.enforcement.severity] < severityRank[overrideBase.enforcement.severity]
  );
  const baselines = draft.startRule.baselineCaptures;
  const calendarRelevant = usesCalendar(draft.expiryRule)
    || [draft.window.warningBefore, draft.window.dueTolerance, draft.window.gracePeriod]
      .some((expression) => expression?.basis === 'CALENDAR_PERIOD');

  const submit = () => {
    setSubmitted(true);
    const normalized: EquipmentCyclePolicy = {
      ...draft,
      code: draft.code.trim().toUpperCase(),
      name: draft.name.trim(),
      startRule: {
        ...draft.startRule,
        ...(draft.startRule.kind === 'FIXED_DATE_TIME'
          ? { fixedDateTime: draft.startRule.fixedDateTime.trim() }
          : {}),
        baselineCaptures: draft.startRule.baselineCaptures.map((capture) => ({
          key: capture.key.trim().toUpperCase(),
          source: capture.source.kind === 'EVENT_FIELD'
            ? { ...capture.source, field: capture.source.field.trim() }
            : { ...capture.source, code: capture.source.code.trim().toUpperCase() },
        })),
      },
      calculationContext: {
        ...draft.calculationContext,
        timeZone: draft.calculationContext.timeZone.trim(),
        adjustmentCalendarCode: draft.calculationContext.adjustmentCalendarCode?.trim() || null,
      },
      enforcement: {
        ...draft.enforcement,
        constraintCode: draft.enforcement.constraintCode.trim().toUpperCase(),
        violationMessageTemplate: draft.enforcement.violationMessageTemplate.trim(),
      },
      note: draft.note?.trim() || null,
    };
    const nextProblems = validateCyclePolicyLocally(normalized);
    const policyCodeExists = !initialPolicy && !overrideBase
      && existingCodes.map((code) => code.toUpperCase()).includes(normalized.code);
    if (policyCodeExists) return;
    if (overrideBase && (
      normalized.code !== overrideBase.code
      || normalized.enforcement.constraintCode !== overrideBase.enforcement.constraintCode
      || (overrideBase.enforcement.hardOrSoft === 'hard' && normalized.enforcement.hardOrSoft !== 'hard')
      || severityRank[normalized.enforcement.severity] < severityRank[overrideBase.enforcement.severity]
    )) return;
    if (nextProblems.length > 0) return;
    onSave(normalized);
  };

  const updateExpiryRule = (nextRule: EquipmentCycleExpiryRule) => {
    setDraft((current) => {
      const synced = syncAccumulatedBaselines(nextRule, current.startRule.baselineCaptures);
      const refreshRule = synced.rule.kind === 'ABSOLUTE_DATE_TIME'
        || (current.refreshRule.kind === 'ROLL_FORWARD_FROM_DUE' && !isRollableRule(synced.rule))
        ? { kind: 'ONE_SHOT' as const }
        : current.refreshRule;
      return {
        ...current,
        expiryRule: synced.rule,
        refreshRule,
        window: {
          ...current.window,
          warningBefore: isPredictableCycleDueRule(synced.rule)
            ? current.window.warningBefore
            : null,
        },
        startRule: { ...current.startRule, baselineCaptures: synced.captures },
      };
    });
  };

  const addBaseline = () => setDraft((current) => ({
    ...current,
    startRule: {
      ...current.startRule,
      baselineCaptures: [
        ...current.startRule.baselineCaptures,
        { key: `BASELINE_${current.startRule.baselineCaptures.length + 1}`, source: { kind: 'METER', code: '' } },
      ],
    },
  }));

  const modalTitle = readOnly ? '查看独立周期约束' : overrideBase ? '编辑单台设备替换策略' : initialPolicy ? '编辑独立周期约束' : '添加独立周期约束';
  const submitLabel = overrideBase ? '保存替换策略' : initialPolicy ? '保存约束' : '添加约束';

  return (
    <WxbModal
      open={open}
      title={modalTitle}
      width={980}
      resizable
      resizeDefaultSize={{ width: 980, height: 760 }}
      okText={submitLabel}
      onCancel={onCancel}
      onOk={submit}
      closable={readOnly || !dirty}
      keyboard={readOnly || !dirty}
      maskClosable={readOnly || !dirty}
      footer={readOnly ? (
        <div className="esc-drawer-actions"><WxbButton type="button" variant="ghost" onClick={onCancel}>关闭</WxbButton></div>
      ) : (
        <div className="esc-drawer-actions">
          {dirty ? (
            <WxbPopconfirm
              title="放弃未保存的独立周期约束修改？"
              description="首个周期、后继周期和到期判定的更改将丢失。"
              okText="放弃修改"
              cancelText="继续编辑"
              onConfirm={onCancel}
            >
              <WxbButton type="button" variant="ghost">取消</WxbButton>
            </WxbPopconfirm>
          ) : <WxbButton type="button" variant="ghost" onClick={onCancel}>取消</WxbButton>}
          <WxbButton type="button" onClick={submit}>{submitLabel}</WxbButton>
        </div>
      )}
      destroyOnClose
    >
      <div className="esc-cycle-policy-modal">
        {overrideBase && (
          <WxbAlert title="单台设备替换保护">
            替换策略必须保持策略编码与约束编码；硬约束不可改为提示或降低严重度。
          </WxbAlert>
        )}
        {submitted && (problems.length > 0 || duplicateCode || overrideViolation) && (
          <WxbAlert variant="error" title="独立周期约束尚不能保存">
            <ul className="esc-problem-list">
              {duplicateCode && <li>策略编码已存在。</li>}
              {overrideViolation && <li>替换策略违反了原策略的编码或诊断强度保护。</li>}
              {problems.map((problem) => <li key={problem.key}>{problem.message}</li>)}
            </ul>
          </WxbAlert>
        )}
        <>
        <div className="esc-form-grid">
          <WxbInput
            label="策略编码"
            value={draft.code}
            disabled={readOnly || Boolean(initialPolicy) || Boolean(overrideBase)}
            placeholder="例如 CIP_RECERTIFICATION"
            helpText="创建后不可修改。"
            onChange={(event) => setDraft((current) => {
              const nextCode = event.target.value;
              const oldDerived = current.code ? `STATE_${current.code.trim().toUpperCase()}_DUE` : '';
              const deriveConstraint = !current.enforcement.constraintCode || current.enforcement.constraintCode === oldDerived;
              return {
                ...current,
                code: nextCode,
                enforcement: deriveConstraint
                  ? { ...current.enforcement, constraintCode: nextCode ? `STATE_${nextCode.trim().toUpperCase()}_DUE` : '' }
                  : current.enforcement,
              };
            })}
          />
          <WxbInput label="策略名称" value={draft.name} disabled={readOnly} placeholder="例如 CIP 定期再确认" onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
        </div>

        <WxbSegmented
          value={view}
          options={[
            { value: 'start', label: '1. 周期开始（首个实例）' },
            { value: 'refresh', label: '2. 后继周期' },
            { value: 'expiry', label: '3. 到期判定' },
          ]}
          onChange={(value) => setView(value as PolicyView)}
        />

        {view === 'start' && (
          <div className="esc-modal-stack">
            <div className="esc-subsection-header"><div><h3>何时创建首个周期义务？</h3><p>由可审计的业务事实创建首个周期实例；这里不会启动 CLOCK 状态时钟。</p></div></div>
            <WxbSelect
              label="首个周期创建方式"
              value={draft.startRule.kind}
              disabled={readOnly}
              options={[
                  { value: 'EVENT', label: '发生业务事实时创建' },
                  { value: 'FIXED_DATE_TIME', label: '在固定时刻创建' },
              ]}
              onChange={(kind) => setDraft((current) => ({
                ...current,
                startRule: kind === 'FIXED_DATE_TIME'
                  ? { kind, fixedDateTime: '', baselineCaptures: current.startRule.baselineCaptures }
                  : {
                    kind: 'EVENT',
                    event: createDefaultCycleEvent(),
                    retriggerPolicy: 'IGNORE_WHILE_ACTIVE',
                    baselineCaptures: current.startRule.baselineCaptures,
                  },
              }))}
            />
            {draft.startRule.kind === 'EVENT' ? (
              <>
                <CycleEventReferenceEditor value={draft.startRule.event} disabled={readOnly} attributes={attributes} actions={actions} onChange={(event) => setDraft((current) => ({ ...current, startRule: current.startRule.kind === 'EVENT' ? { ...current.startRule, event } : current.startRule }))} />
                <WxbSelect
                  label="首个周期已存在时再次收到事件"
                  value={draft.startRule.retriggerPolicy}
                  disabled={readOnly}
                  options={[
                    { value: 'IGNORE_WHILE_ACTIVE', label: '忽略，保持当前周期' },
                    { value: 'RESTART', label: '重置当前周期起点' },
                    { value: 'REJECT', label: '拒绝并记录异常' },
                  ]}
                  onChange={(retriggerPolicy) => setDraft((current) => ({
                    ...current,
                    startRule: current.startRule.kind === 'EVENT'
                      ? { ...current.startRule, retriggerPolicy: retriggerPolicy as typeof current.startRule.retriggerPolicy }
                      : current.startRule,
                  }))}
                />
              </>
            ) : (
              <WxbInput
                label="首个周期固定创建时刻"
                value={draft.startRule.fixedDateTime}
                disabled={readOnly}
                placeholder="2026-07-11T08:00:00+08:00"
                helpText="请填写带时区偏移的完整时间。"
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  startRule: current.startRule.kind === 'FIXED_DATE_TIME'
                    ? { ...current.startRule, fixedDateTime: event.target.value }
                    : current.startRule,
                }))}
              />
            )}

            {(hasAccumulatedRule(draft.expiryRule) || baselines.length > 0) && (
              <div className="esc-subsection">
                <div className="esc-subsection-header">
                  <div><h3>创建首个周期时采集基线</h3><p>仪表或信号累计增量以这里采集的读数为起点。</p></div>
                  {!readOnly && <WxbButton type="button" size="sm" variant="secondary" onClick={addBaseline}>添加基线</WxbButton>}
                </div>
                {baselines.map((capture, index) => (
                  <div className="esc-form-grid esc-form-grid-four" key={`${capture.key}-${index}`}>
                    <WxbInput label="基线编码" value={capture.key} disabled={readOnly} onChange={(event) => setDraft((current) => ({ ...current, startRule: { ...current.startRule, baselineCaptures: current.startRule.baselineCaptures.map((item, itemIndex) => itemIndex === index ? { ...item, key: event.target.value } : item) } }))} />
                    <WxbSelect
                      label="数据来源"
                      value={capture.source.kind}
                      disabled={readOnly}
                      options={[
                        { value: 'METER', label: '仪表' }, { value: 'SIGNAL', label: '信号' },
                        { value: 'EVENT_FIELD', label: '启动事件字段' },
                      ]}
                      onChange={(kind) => setDraft((current) => {
                        const source: EquipmentCycleBaselineSource = kind === 'EVENT_FIELD'
                          ? { kind, field: '' }
                          : { kind: kind as 'METER' | 'SIGNAL', code: '' };
                        return { ...current, startRule: { ...current.startRule, baselineCaptures: current.startRule.baselineCaptures.map((item, itemIndex) => itemIndex === index ? { ...item, source } : item) } };
                      })}
                    />
                    <WxbInput
                      label={capture.source.kind === 'EVENT_FIELD' ? '事件字段' : '来源编码'}
                      value={capture.source.kind === 'EVENT_FIELD' ? capture.source.field : capture.source.code}
                      disabled={readOnly}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        startRule: {
                          ...current.startRule,
                          baselineCaptures: current.startRule.baselineCaptures.map((item, itemIndex) => itemIndex === index
                            ? { ...item, source: item.source.kind === 'EVENT_FIELD' ? { ...item.source, field: event.target.value } : { ...item.source, code: event.target.value } }
                            : item),
                        },
                      }))}
                    />
                    {!readOnly && (
                      <WxbButton type="button" size="sm" variant="ghost" onClick={() => setDraft((current) => ({ ...current, startRule: { ...current.startRule, baselineCaptures: current.startRule.baselineCaptures.filter((_, itemIndex) => itemIndex !== index) } }))}>
                        删除基线
                      </WxbButton>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'refresh' && (
          <div className="esc-modal-stack">
            <div className="esc-subsection-header"><div><h3>当前周期满足后怎样创建后继周期？</h3><p>配置后继周期实例的创建方式，不涉及 CLOCK 的 START_TIMER / CLEAR_TIMER。</p></div></div>
            <WxbSelect
              label="后继周期方式"
              value={draft.refreshRule.kind}
              disabled={readOnly || draft.expiryRule.kind === 'ABSOLUTE_DATE_TIME'}
              options={[
                { value: 'ONE_SHOT', label: '仅执行一次' },
                { value: 'RESET_ON_EVENT', label: '完成或发生事件后重新起算' },
                { value: 'ROLL_FORWARD_FROM_DUE', label: '从上次到期点继续推进', disabled: !isRollableRule(draft.expiryRule) },
              ]}
              onChange={(kind) => setDraft((current) => ({
                ...current,
                refreshRule: kind === 'RESET_ON_EVENT'
                  ? { kind, event: { kind: 'CYCLE_SATISFIED' }, recaptureBaselines: true }
                  : kind === 'ROLL_FORWARD_FROM_DUE'
                    ? { kind, missedCyclePolicy: 'CATCH_UP_LATEST' }
                    : { kind: 'ONE_SHOT' },
              }))}
            />
            {draft.expiryRule.kind === 'ABSOLUTE_DATE_TIME' && (
              <WxbAlert title="固定截止时刻仅执行一次">
                如需周期性到期，请在第三步改用连续时长、自然日历周期或日历排期。
              </WxbAlert>
            )}
            {draft.refreshRule.kind === 'RESET_ON_EVENT' && (
              <>
                <CycleEventReferenceEditor value={draft.refreshRule.event} disabled={readOnly} attributes={attributes} actions={actions} onChange={(event) => setDraft((current) => ({ ...current, refreshRule: current.refreshRule.kind === 'RESET_ON_EVENT' ? { ...current.refreshRule, event } : current.refreshRule }))} />
                {baselines.length > 0 && (
                  <WxbCheckbox checked={draft.refreshRule.recaptureBaselines} disabled={readOnly} onChange={(recaptureBaselines) => setDraft((current) => ({ ...current, refreshRule: current.refreshRule.kind === 'RESET_ON_EVENT' ? { ...current.refreshRule, recaptureBaselines } : current.refreshRule }))}>
                    新周期开始时重新采集累计基线
                  </WxbCheckbox>
                )}
              </>
            )}
            {draft.refreshRule.kind === 'ROLL_FORWARD_FROM_DUE' && (
              <WxbSelect
                label="错过一个或多个到期点时"
                value={draft.refreshRule.missedCyclePolicy}
                disabled={readOnly}
                options={[
                  { value: 'CATCH_UP_ALL', label: '逐个补齐所有周期' },
                  { value: 'CATCH_UP_LATEST', label: '只处理最近一个周期' },
                  { value: 'SKIP', label: '跳过已错过的周期' },
                ]}
                onChange={(missedCyclePolicy) => setDraft((current) => ({
                  ...current,
                  refreshRule: current.refreshRule.kind === 'ROLL_FORWARD_FROM_DUE'
                    ? { ...current.refreshRule, missedCyclePolicy: missedCyclePolicy as typeof current.refreshRule.missedCyclePolicy }
                    : current.refreshRule,
                }))}
              />
            )}
          </div>
        )}

        {view === 'expiry' && (
          <div className="esc-modal-stack">
            <div className="esc-subsection-header"><div><h3>什么情况下判定到期？</h3><p>按选择逐步填写；支持连续时间、自然日历、业务日历、仪表/信号累计增量与复合条件。</p></div></div>
            <CycleRuleEditor
              rule={draft.expiryRule}
              disabled={readOnly}
              baselineCaptures={baselines}
              attributes={attributes}
              actions={actions}
              onChange={updateExpiryRule}
            />
            <div className="esc-form-grid">
              <WxbSelect
                label="到期后处理"
                value={draft.enforcement.hardOrSoft}
                disabled={readOnly || overrideBase?.enforcement.hardOrSoft === 'hard'}
                options={[
                  { value: 'hard', label: '阻断排产，直到约束解除' },
                  { value: 'soft', label: '仅提示风险' },
                ]}
                onChange={(hardOrSoft) => setDraft((current) => ({ ...current, enforcement: { ...current.enforcement, hardOrSoft: hardOrSoft as EquipmentStateIssueHardness } }))}
              />
              <WxbTextarea label="策略说明（可选）" value={draft.note ?? ''} disabled={readOnly} placeholder="记录策略来源、适用边界和审核说明。" onChange={(event) => setDraft((current) => ({ ...current, note: event.target.value }))} />
            </div>

            <WxbCollapse
              items={[
                {
                  key: 'window',
                  label: '预警、容差与宽限设置',
                  children: (
                    <div className="esc-cycle-window-grid">
                      {isPredictableCycleDueRule(draft.expiryRule) ? (
                        <OptionalCycleTime label="预警提前量" value={draft.window.warningBefore} disabled={readOnly} onChange={(warningBefore) => setDraft((current) => ({ ...current, window: { ...current.window, warningBefore } }))} />
                      ) : (
                        <div className="esc-form-span">
                          <WxbAlert title="当前规则无法提前计算明确到期时刻">
                            因此不提供提前预警配置；连续时长、自然日历、日历排期、固定截止时刻及全部条件可预测的组合规则支持此功能。
                          </WxbAlert>
                        </div>
                      )}
                      <OptionalCycleTime label="到期容差" value={draft.window.dueTolerance} disabled={readOnly} onChange={(dueTolerance) => setDraft((current) => ({ ...current, window: { ...current.window, dueTolerance } }))} />
                      <OptionalCycleTime label="宽限窗口" value={draft.window.gracePeriod} disabled={readOnly} onChange={(gracePeriod) => setDraft((current) => ({ ...current, window: { ...current.window, gracePeriod }, enforcement: !gracePeriod && current.enforcement.activationPoint === 'GRACE_END' ? { ...current.enforcement, activationPoint: 'DUE' } : current.enforcement }))} />
                      <WxbSelect
                        label="约束生效时点"
                        value={draft.enforcement.activationPoint}
                        disabled={readOnly}
                        options={[
                          { value: 'DUE', label: '到期即生效' },
                          { value: 'GRACE_END', label: '宽限结束后生效', disabled: !draft.window.gracePeriod },
                        ]}
                        onChange={(activationPoint) => setDraft((current) => ({ ...current, enforcement: { ...current.enforcement, activationPoint: activationPoint as typeof current.enforcement.activationPoint } }))}
                      />
                    </div>
                  ),
                },
                ...(calendarRelevant ? [{
                  key: 'calendar',
                  label: '业务日历调整',
                  children: (
                    <div className="esc-form-grid esc-form-grid-three">
                      <WxbSelect
                        label="到期日遇非工作日"
                        value={draft.calculationContext.businessDayAdjustment}
                        disabled={readOnly}
                        options={[
                          { value: 'NONE', label: '不调整' }, { value: 'FOLLOWING', label: '顺延到下一工作日' },
                          { value: 'PRECEDING', label: '提前到上一工作日' }, { value: 'MODIFIED_FOLLOWING', label: '修正顺延（不跨月）' },
                          { value: 'MODIFIED_PRECEDING', label: '修正提前（不跨月）' }, { value: 'SKIP', label: '非工作日则跳过' },
                        ]}
                        onChange={(businessDayAdjustment) => setDraft((current) => ({
                          ...current,
                          calculationContext: {
                            ...current.calculationContext,
                            businessDayAdjustment: businessDayAdjustment as typeof current.calculationContext.businessDayAdjustment,
                            adjustmentCalendarCode: businessDayAdjustment === 'NONE' ? null : current.calculationContext.adjustmentCalendarCode,
                          },
                        }))}
                      />
                      {draft.calculationContext.businessDayAdjustment !== 'NONE' && (
                        <WxbInput label="调整日历编码" value={draft.calculationContext.adjustmentCalendarCode ?? ''} disabled={readOnly} placeholder="例如 CN-SH-WORKDAY" onChange={(event) => setDraft((current) => ({ ...current, calculationContext: { ...current.calculationContext, adjustmentCalendarCode: event.target.value } }))} />
                      )}
                    </div>
                  ),
                }] : []),
                {
                  key: 'diagnostic',
                  label: '诊断与追踪设置',
                  children: (
                    <div className="esc-form-grid esc-form-grid-three">
                      <WxbInput label="约束编码" value={draft.enforcement.constraintCode} disabled={readOnly || Boolean(overrideBase)} onChange={(event) => setDraft((current) => ({ ...current, enforcement: { ...current.enforcement, constraintCode: event.target.value } }))} />
                      <WxbSelect
                        label="严重度"
                        value={draft.enforcement.severity}
                        disabled={readOnly}
                        options={(['info', 'warning', 'critical'] as EquipmentStateIssueSeverity[])
                          .filter((severity) => !overrideBase || severityRank[severity] >= severityRank[overrideBase.enforcement.severity])
                          .map((value) => ({ value, label: value === 'critical' ? '严重' : value === 'warning' ? '警告' : '提示' }))}
                        onChange={(severity) => setDraft((current) => ({ ...current, enforcement: { ...current.enforcement, severity: severity as EquipmentStateIssueSeverity } }))}
                      />
                      <div className="esc-form-span">
                        <WxbTextarea label="违规消息模板" value={draft.enforcement.violationMessageTemplate} disabled={readOnly} helpText="需含资源字段与到期时间字段，便于定位。" onChange={(event) => setDraft((current) => ({ ...current, enforcement: { ...current.enforcement, violationMessageTemplate: event.target.value } }))} />
                      </div>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
        </>
      </div>
    </WxbModal>
  );
};

export default CyclePolicyModal;
